// frontend/src/pages/historias/VistaHCE.jsx - VERSIÓN FINAL CON SOLUCIÓN DE ESTADO

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, Grid,
  Avatar, Chip, Button, CircularProgress, Alert,
  Paper, IconButton, Tooltip, Container,
  useTheme, useMediaQuery, Card, CardContent,
  Stack, Divider, Fade, Grow, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  MedicalServices as MedicalServicesIcon,
  Psychology as PsychologyIcon,
  Science as ScienceIcon,
  Healing as HealingIcon,
  FavoriteRounded as FavoriteRoundedIcon,
  CalendarMonth as CalendarMonthIcon,
  LocalPhone as PhoneIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Cake as CakeIcon,
  Wc as GenderIcon,
  Assignment as AssignmentIcon,
  FolderSpecial as FolderIcon,
  EventNote as EventNoteIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNotification } from '../../contexts/NotificationContext';
 
import historiaService from '../../services/historiaService';
import PageHeader from '../../components/common/PageHeader';
import { formatDate } from '../../utils/dateUtils';
import EspecialidadTabContent from '../../components/historias/EspecialidadTabContent';
import SesionesEtapaPaciente from '../../components/historias/SesionesEtapaPaciente';

// 🎯 FUNCIONES PARA GUARDAR Y RESTAURAR ESTADO
const guardarEstadoTab = (tabValue, pacienteId) => {
  const estado = {
    tabValue,
    timestamp: Date.now(),
    pacienteId
  };
  sessionStorage.setItem('vistaHCE_estado', JSON.stringify(estado));
 };

const restaurarEstadoTab = (pacienteId, totalTabs) => {
  try {
    const estadoGuardado = sessionStorage.getItem('vistaHCE_estado');
    if (estadoGuardado) {
      const estado = JSON.parse(estadoGuardado);
      
      // Verificar que sea del mismo paciente y no muy antiguo (5 minutos)
      const esValido = estado.pacienteId === pacienteId && 
                      (Date.now() - estado.timestamp) < 300000;
      
      if (esValido && estado.tabValue < totalTabs) {
         // Limpiar después de usar
        sessionStorage.removeItem('vistaHCE_estado');
        return estado.tabValue;
      }
    }
  } catch (error) {
    console.error('Error al restaurar estado:', error);
  }
  return 0; // Tab por defecto
};

// Estilos mejorados
const MainContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
  paddingBottom: theme.spacing(3),
}));

const TabsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  marginBottom: theme.spacing(2),
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(1.5),
    borderRadius: '8px',
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: '#7b1fa2',
    height: 3,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '13px',
    letterSpacing: '0.3px',
    color: '#64748b',
    minHeight: 48,
    padding: theme.spacing(1, 2.5),
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
      minHeight: 44,
      padding: theme.spacing(1, 2),
      minWidth: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
      padding: theme.spacing(1, 1.5),
    },
    '&.Mui-selected': {
      color: '#7b1fa2',
      fontWeight: 700,
    },
    '&:hover': {
      color: '#7b1fa2',
      backgroundColor: 'rgba(123, 31, 162, 0.04)',
    }
  }
}));

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(1.5),
    borderRadius: '8px',
  },
}));

const InfoSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const PatientHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    textAlign: 'center',
    gap: theme.spacing(1.5),
  },
}));

const InfoGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1),
  },
}));

const InfoCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: '10px',
  backgroundColor: '#f8fafc',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f1f5f9',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f3e8ff',
  flexShrink: 0,
}));

const EspecialidadItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(0.75),
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: '#7b1fa2',
    transform: 'translateX(4px)',
  },
  '&:last-child': {
    marginBottom: 0,
  },
}));

// Función para obtener icono de especialidad
const getEspecialidadIcon = (nombreEspecialidad) => {
  const nombre = nombreEspecialidad?.toLowerCase() || '';
  
  if (nombre.includes('psicolog') || nombre.includes('psico')) {
    return <PsychologyIcon sx={{ fontSize: '18px' }} />;
  }
  if (nombre.includes('neuro')) {
    return <ScienceIcon sx={{ fontSize: '18px' }} />;
  }
  if (nombre.includes('cardio')) {
    return <FavoriteRoundedIcon sx={{ fontSize: '18px' }} />;
  }
  if (nombre.includes('medicina') || nombre.includes('general')) {
    return <HealingIcon sx={{ fontSize: '18px' }} />;
  }
  
  return <MedicalServicesIcon sx={{ fontSize: '18px' }} />;
};

// Componente TabPanel
function TabPanel({ children, value, index, ...other }) {
  return (
    <Fade in={value === index} timeout={300}>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
        {...other}
      >
        {value === index && <Box>{children}</Box>}
      </div>
    </Fade>
  );
}

const VistaHCE = () => {
  const { pacienteId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historiaUnificada, setHistoriaUnificada] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // 🔧 Función para manejar cambio de tab y guardar estado
  const handleTabChange = (event, newValue) => {
     setTabValue(newValue);
    // 🔧 GUARDAR ESTADO cada vez que cambie el tab
    guardarEstadoTab(newValue, pacienteId);
  };

  // 🔧 Función para manejar actualizaciones desde formularios
  const handleFormularioUpdate = () => {
  // 🔥 NO HACER NADA SI EL SISTEMA ESTÁ BLOQUEADO
  if (window.sistemaBloqueoRecargas?.estaBloqueado()) {
     return;
  }
  
   guardarEstadoTab(tabValue, pacienteId);
  setRefreshKey(prev => prev + 1);
};

  // Función de refrescar datos
  const handleRefresh = () => {
     setRefreshKey(prev => prev + 1);
  };

  // Cargar historia unificada por paciente
  useEffect(() => {
  const cargarHistoriaUnificada = async () => {
    // 🔥 VERIFICAR BLOQUEO DEL SISTEMA ANTES DE CARGAR
    if (window.sistemaBloqueoRecargas?.estaBloqueado()) {
       return;
    }
    
    if (!pacienteId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await historiaService.getHistoriasPorPaciente(pacienteId);
      
      if (!response || !response.data) {
        throw new Error('No se encontró historia clínica para este paciente');
      }
      
      setHistoriaUnificada(response.data);
      
      // 🔥 SOLO restaurar estado en carga inicial o cuando NO esté bloqueado
      if (!window.sistemaBloqueoRecargas?.estaBloqueado()) {
        const totalTabs = 1 + (response.data?.especialidades?.length || 0);
        const tabRestaurado = restaurarEstadoTab(pacienteId, totalTabs);
        
         setTabValue(tabRestaurado);
      } else {
       }
      
    } catch (error) {
      console.error('Error al cargar historia unificada:', error);
      setError(error.message || 'Error al cargar la historia clínica');
      showNotification('Error al cargar la historia clínica', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  cargarHistoriaUnificada();
}, [pacienteId, refreshKey, showNotification]);

  // Renderizado de carga
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Renderizado de error
  if (error) {
    return (
      <Container maxWidth="xl">
        <Box p={3} textAlign="center">
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/historias')}
          >
            Volver a Historias
          </Button>
        </Box>
      </Container>
    );
  }

  // Verificar datos
  if (!historiaUnificada) {
    return (
      <Container maxWidth="xl">
        <Box p={3} textAlign="center">
          <Alert severity="info" sx={{ mb: 3 }}>
            No se encontró historia clínica para este paciente
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/historias')}
          >
            Volver a Historias
          </Button>
        </Box>
      </Container>
    );
  }

  // Acceso seguro a datos
  const paciente = historiaUnificada.paciente || {};
  const especialidades = historiaUnificada.especialidades || [];

  // Datos del paciente
  const datosDelPaciente = {
    nombre: paciente.nombres && paciente.apellidos ? 
      `${paciente.nombres} ${paciente.apellidos}`.toUpperCase() : 
      'PACIENTE',
    edad: paciente.edad || 0,
    identificacion: paciente.dni || 'Sin DNI',
    fechaNacimiento: paciente.fecha_nacimiento ? 
      formatDate(paciente.fecha_nacimiento) : 
      'No registrada',
    sexo: paciente.sexo === 'M' ? 'Masculino' : 
          paciente.sexo === 'F' ? 'Femenino' : 
          'No especificado',
    telefono: paciente.telefono || 'No registrado',
    email: paciente.email || 'No registrado'
  };

  
  return (
    <MainContainer>
      {/* Header con breadcrumb */}
      <PageHeader
        title={`${paciente.numero_historia || 'HCE-000001'}-${paciente.nombres || 'HCE-000001'}`}
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Historias Clínicas', to: '/historias' },
          { text: 'Vista HCE' }
        ]}
        actionButton={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/historias')}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => showNotification('Funcionalidad de impresión en desarrollo', 'info')}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Imprimir
            </Button>
          </Stack>
        }
      />

      {/* Sistema de Tabs */}
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        <TabsContainer>
          <StyledTabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab 
              icon={<PersonIcon />}
              iconPosition="start"
              label={isTablet || isMobile ? "General" : "INFORMACIÓN GENERAL"}
            />
            {especialidades.map((esp) => (
              <Tab 
                key={esp.id}
                icon={getEspecialidadIcon(esp.especialidad?.nombre)}
                iconPosition="start"
                label={isTablet || isMobile ? 
                  esp.especialidad?.nombre?.split(' ')[0]?.toUpperCase() : 
                  esp.especialidad?.nombre?.toUpperCase() || 'ESPECIALIDAD'
                }
              />
            ))}
          </StyledTabs>
        </TabsContainer>

        {/* Contenido de los Tabs */}
        {/* Tab de información general */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            {/* Columna Izquierda - Información del Paciente y General */}
            <Grid item xs={12} lg={8}>
              {/* Card de Información del Paciente */}
              <Grow in timeout={500}>
                <MainCard>
                  <InfoSection>
                    <PatientHeader>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#7b1fa2', 
                          width: isMobile ? 60 : 72, 
                          height: isMobile ? 60 : 72,
                          fontSize: isMobile ? '1.5rem' : '2rem',
                          boxShadow: '0 4px 12px rgba(123, 31, 162, 0.3)',
                        }}
                      >
                        {datosDelPaciente.nombre.charAt(0)}
                      </Avatar>
                      
                      <Box flex={1}>
                        <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
                          fontWeight: 700, 
                          color: '#1e293b',
                          mb: 0.5
                        }}>
                          {datosDelPaciente.nombre}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                          <Chip 
                            label={`${datosDelPaciente.edad} años`}
                            size="small"
                            sx={{ bgcolor: '#f1f5f9' }}
                          />
                          <Chip 
                            label={`DNI: ${datosDelPaciente.identificacion}`}
                            size="small"
                            sx={{ bgcolor: '#f1f5f9' }}
                          />
                        </Stack>
                      </Box>

                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={() => navigate(`/pacientes/${pacienteId}`)}
                        sx={{ 
                          borderRadius: '10px',
                          textTransform: 'none',
                          bgcolor: '#7b1fa2',
                          px: 2,
                          py: 0.75,
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: '#6a1b9a'
                          }
                        }}
                      >
                        Ver Perfil
                      </Button>
                    </PatientHeader>
                    
                    <InfoGrid>
                      <InfoCard>
                        <IconWrapper>
                          <CakeIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                        </IconWrapper>
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Fecha de Nacimiento
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {datosDelPaciente.fechaNacimiento}
                          </Typography>
                        </Box>
                      </InfoCard>
                      
                      <InfoCard>
                        <IconWrapper>
                          <GenderIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                        </IconWrapper>
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Sexo
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {datosDelPaciente.sexo}
                          </Typography>
                        </Box>
                      </InfoCard>
                      
                      <InfoCard>
                        <IconWrapper>
                          <PhoneIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                        </IconWrapper>
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Teléfono
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {datosDelPaciente.telefono}
                          </Typography>
                        </Box>
                      </InfoCard>
                      
                      <InfoCard>
                        <IconWrapper>
                          <EmailIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                        </IconWrapper>
                        <Box flex={1}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Email
                          </Typography>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {datosDelPaciente.email}
                          </Typography>
                        </Box>
                      </InfoCard>
                    </InfoGrid>
                  </InfoSection>
                </MainCard>
              </Grow>

              {/* Card de Información General de la Historia */}
              <Grow in timeout={600}>
                <MainCard>
                  <InfoSection>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <IconWrapper sx={{ width: 40, height: 40 }}>
                        <AssignmentIcon sx={{ color: '#7b1fa2', fontSize: 24 }} />
                      </IconWrapper>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Información General de la Historia
                      </Typography>
                    </Box>
                    
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="caption" color="textSecondary" gutterBottom sx={{ 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}>
                          Motivo de Consulta General
                        </Typography>
                        <Paper sx={{ 
                          p: 2, 
                          bgcolor: '#f8fafc', 
                          borderRadius: '10px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#475569' }}>
                            {historiaUnificada.motivo_consulta_general || 'Historia clínica unificada del paciente para seguimiento integral de su proceso terapéutico.'}
                          </Typography>
                        </Paper>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <InfoCard>
                            <IconWrapper>
                              <CalendarMonthIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                            </IconWrapper>
                            <Box>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Fecha de Apertura
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {historiaUnificada.fecha_apertura ? 
                                  formatDate(historiaUnificada.fecha_apertura) : 
                                  formatDate(new Date())
                                }
                              </Typography>
                            </Box>
                          </InfoCard>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <InfoCard>
                            <IconWrapper sx={{ bgcolor: '#e8f5e9' }}>
                              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                            </IconWrapper>
                            <Box>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Estado de la Historia
                              </Typography>
                              <Chip 
                                label={historiaUnificada.estado || 'Activo'} 
                                color="success"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </InfoCard>
                        </Grid>
                      </Grid>
                    </Stack>
                  </InfoSection>
                </MainCard>
              </Grow>
            </Grid>

            {/* Columna Derecha - Especialidades */}
            <Grid item xs={12} lg={4}>
              <Grow in timeout={700}>
                <MainCard>
                  <InfoSection>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <IconWrapper sx={{ width: 40, height: 40 }}>
                          <MedicalServicesIcon sx={{ color: '#7b1fa2', fontSize: 24 }} />
                        </IconWrapper>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Especialidades Asignadas
                        </Typography>
                      </Box>
                      <Chip 
                        label={especialidades.length} 
                        sx={{ 
                          bgcolor: '#f3e8ff', 
                          color: '#7b1fa2',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    
                    {especialidades.length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        No hay especialidades asignadas a este paciente
                      </Alert>
                    ) : (
                      <List sx={{ p: 0 }}>
                        {especialidades.map((esp, index) => (
                          <EspecialidadItem
                            key={esp.id}
                            onClick={() => handleTabChange(null, index + 1)}
                            secondaryAction={
                              <IconButton edge="end" size="small">
                                <ArrowForwardIosIcon fontSize="small" />
                              </IconButton>
                            }
                          >
                            <ListItemIcon>
                              <IconWrapper>
                                {React.cloneElement(getEspecialidadIcon(esp.especialidad?.nombre), {
                                  sx: { color: '#7b1fa2', fontSize: 18 }
                                })}
                              </IconWrapper>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={600}>
                                  {esp.especialidad?.nombre || 'Especialidad'}
                                </Typography>
                              }
                              secondary={
                                <Stack spacing={0.5} mt={0.5}>
                                  <Chip 
                                    label={esp.estado === 'En tratamiento' ? 'En tratamiento' : 'En evaluación'} 
                                    size="small"
                                    color={esp.estado === 'En tratamiento' ? 'success' : 'warning'}
                                    sx={{ width: 'fit-content', height: 20, fontSize: '0.75rem' }}
                                  />
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                    Creado: {esp.fecha_creacion ? formatDate(esp.fecha_creacion) : formatDate(new Date())}
                                  </Typography>
                                </Stack>
                              }
                            />
                          </EspecialidadItem>
                        ))}
                      </List>
                    )}
                  </InfoSection>
                </MainCard>
              </Grow>
            </Grid>
          </Grid>

          {/* Sesiones de Etapa - Ancho completo */}
          <Box mt={2}>
            <Grow in timeout={800}>
              <MainCard>
                <InfoSection>
                  <SesionesEtapaPaciente 
                    pacienteId={pacienteId} 
                    historiaId={historiaUnificada.id}
                  />
                </InfoSection>
              </MainCard>
            </Grow>
          </Box>
        </TabPanel>

        {/* Tabs de especialidades */}
        {especialidades.map((esp, index) => (
          <TabPanel key={esp.id} value={tabValue} index={index + 1}>
            <Fade in timeout={300}>
              <Box>
                <EspecialidadTabContent
                  seccionEspecialidad={esp}
                  historiaUnificadaId={historiaUnificada.id}
                  pacienteId={pacienteId}
                  onUpdate={handleFormularioUpdate}
                />
              </Box>
            </Fade>
          </TabPanel>
        ))}
      </Container>
    </MainContainer>
  );
};

export default VistaHCE;  